import { BIZ_LINE } from '@whelk/const/src/appInfo';
import axios, { type CustomAxiosRequestConfig } from '@whelk/request/src/services/axios';
import type { ApiResp } from '@whelk/services/src/common';

export interface UserInfo {
  userID: string;
  realUserID: string;
  isAnonymous?: boolean;
  userName: string;
  username?: string;
  webAvatar: {
    small: string;
    medium: string;
    large: string;
    oversize: string;
  };
}

export interface UserNewInfo {
  userID: string;
  realUserID: string;
  name: string;
  phone: string;
  avatarInfo: {
    small: string;
    medium: string;
    large: string;
    oversize: string;
  };
}

export const IS_REGISTER = {
  YES: 1, // 新用户
  OLD_USER: 0,
} as const;

export interface COUNTRY_CODE_OPTION {
  capital: string;
  codelist: {
    value: string;
    name: string;
  }[];
}

export type IsRegisterEnum = (typeof IS_REGISTER)[keyof typeof IS_REGISTER];

export type LoginResBody = UserInfo & {
  token: string;
  isRegister: IsRegisterEnum;
  adInfo?: Record<string, string>;
  username: string;
};

export interface SendUserInfoDto {
  sexual: string;
  education: string;
  occupation: string;
  age: string;
  phone: string;
}

export interface WXUnionModel {
  openID: string;
  unionID: string;
}

export enum LOGIN_TYPE {
  LOGINTYPEPHONE = '',
  LOGINTYPEANDROIDONECLICK = '1',
  LOGINTYPEIOSONECLICK = '2',
  LOGINTYPEANONYMOUS = '3',
  /*  微信第三方登录 */
  LOGINTYPETHIRDPART = '5',
  /*  firebase登录 */
  FIREBASE = '11',
}

export const DEFAULT_COUNTRY_CODE = '+86';

export async function postSendPhoneCode(params: {
  phone: string;
  countryCode?: string;
  unionID?: string;
  ticket?: string;
  randStr?: string;
}) {
  params.countryCode = DEFAULT_COUNTRY_CODE;
  const url = `/v1/api/user/login/sms/send`;
  const { data: resp } = await axios.post<ApiResp<null>>(url, params);
  return resp.data;
}

export async function postApplicationSendPhoneCode(params: { phone: string; appID?: number }) {
  const url = `/v1/api/user/submit_application/sms/send`;
  const { data: resp } = await axios.post<ApiResp<null>>(url, params);
  return resp.data;
}

export async function getUnionIdOpenId(params: { code: string }) {
  const url = `/v1/api/user/getOpenData`;
  const { data: resp } = await axios.post<ApiResp<WXUnionModel>>(url, params);
  return resp.data;
}

export async function postLoginByWx(params: {
  unionId: string;
  loginType: (typeof LOGIN_TYPE)[keyof typeof LOGIN_TYPE];
}) {
  const url = `/v1/api/user/login/phone`;
  const { data: resp } = await axios.post<ApiResp<LoginResBody>>(url, {
    ...params,
  });
  return resp.data;
}

export async function postLoginByPhoneCode(params: {
  phone?: string;
  code?: string;
  unionId?: string;
  countryCode?: string;
  loginType?: (typeof LOGIN_TYPE)[keyof typeof LOGIN_TYPE];
  adInfo?: Record<string, string>;
}) {
  params.countryCode = DEFAULT_COUNTRY_CODE;
  const url = `/v1/api/user/login/phone`;
  const { data: resp } = await axios.post<ApiResp<LoginResBody>>(url, {
    ...params,
    code: params.code ? String(params.code) : undefined,
  });
  return resp.data;
}

export async function getCurrentUser() {
  const url = `/v1/api/user/info`;
  const { data: resp } = await axios.get<ApiResp<{ userInfo: UserNewInfo }>>(url);
  return resp.data;
}

// 获取开放平台跳转的token
export async function getOpenPlatformToken(openPlatformKey: string) {
  const url = `/v1/api/user/login/open`;
  const { data: resp } = await axios.post<ApiResp<{ token: string }>>(
    url,
    {
      openPlatformKey: openPlatformKey,
    },
    { showToast: false } as CustomAxiosRequestConfig,
  );
  return resp.data;
}

// token 续期
export async function getNewToken() {
  const url = `/v1/api/user/renewal`;
  const { data: resp } = await axios.post<ApiResp<{ token: string }>>(url);
  return resp.data;
}

export async function getDeviceIdToUuid(uuid: string) {
  const url = `/v1/api/user/device/register`;
  const { data: resp } = await axios.post<
    ApiResp<{
      deviceIDStr: string;
      userID: string;
      realUserID: string;
    }>
  >(url, { uuid });
  return resp.data;
}

export async function postLogout() {
  const url = `/v1/api/user/logout`;
  const { data: resp } = await axios.post<ApiResp<null>>(url);
  return resp.data;
}

// 用户封禁申诉
export async function postBanUserAppeal(phone: string) {
  const url = `/public/app/ban_user_appeal`;
  const { data: resp } = await axios.post<ApiResp<null>>(url, { phone });
  return resp.data;
}

// export async function getCountryCodeList() {
//   const url = `/v1/api/location/get_country_code_list`;
//   const { data: resp } = await axios.get<ApiResp<COUNTRY_CODE_OPTION[]>>(url);
//   return resp.data;
// }

export async function postGetCaptchaInfo(params: { phone: string; countryCode?: string }) {
  params.countryCode = DEFAULT_COUNTRY_CODE;
  const url = `/v1/api/user/need_captcha`;
  const { data: resp } = await axios.post<
    ApiResp<{
      needCaptcha: boolean;
      aid: string;
      aidEncrypted: string;
    }>
  >(url, params);
  return resp.data;
}

export async function getMigrateInfo() {
  const url = `/v1/migrate/video/need_migrate`;
  const { data: resp } = await axios.post<
    ApiResp<{
      needMigrate: boolean;
      token: string;
    }>
  >(url);
  return resp.data;
}

export async function postLoginByFirebase(params: { authToken: string }) {
  const url = `/v1/api/user/login/phone`;
  const { data: resp } = await axios.post<ApiResp<LoginResBody>>(url, {
    loginType: LOGIN_TYPE.FIREBASE,
    authToken: params.authToken,
  });
  return resp.data;
}
export const postBannedAppeal = async (params?: { email: string }) => {
  const url = `/v1/api/safety/user/appeal`;
  const { data: resp } = await axios.post<ApiResp<null>>(url, {
    email: params?.email,
    bizLines: [BIZ_LINE.TEXT],
  });
  return resp.statusInfo;
};
